import React, { useState } from 'react';
import { X, Upload, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CreateVehicleModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        category: 'Sedan',
        daily_rate: '',
        features: [],
        city: '',
        images: []
    });

    const [newFeature, setNewFeature] = useState('');
    const [loading, setLoading] = useState(false);

    const categories = ['Sedan', 'SUV', 'Compact', 'Luxury', 'Van', 'Truck'];
    const commonFeatures = ['GPS', 'Climatisation', 'Bluetooth', 'USB', 'Caméra de recul', 'Sièges chauffants', 'Toit ouvrant'];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.make || !formData.model || !formData.daily_rate || !formData.city) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('http://localhost:8001/api/agencies/my/vehicles', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    daily_rate: parseInt(formData.daily_rate)
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la création du véhicule');
            }

            toast.success('Véhicule créé avec succès!');
            onSuccess();
            onClose();

            // Reset form
            setFormData({
                make: '',
                model: '',
                year: new Date().getFullYear(),
                category: 'Sedan',
                daily_rate: '',
                features: [],
                city: '',
                images: []
            });
        } catch (error) {
            console.error('Error creating vehicle:', error);
            toast.error('Erreur lors de la création du véhicule');
        } finally {
            setLoading(false);
        }
    };

    const addFeature = () => {
        if (newFeature && !formData.features.includes(newFeature)) {
            setFormData({
                ...formData,
                features: [...formData.features, newFeature]
            });
            setNewFeature('');
        }
    };

    const removeFeature = (feature) => {
        setFormData({
            ...formData,
            features: formData.features.filter(f => f !== feature)
        });
    };

    const addCommonFeature = (feature) => {
        if (!formData.features.includes(feature)) {
            setFormData({
                ...formData,
                features: [...formData.features, feature]
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 p-6 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-2xl font-bold text-white">Ajouter un véhicule</h2>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Marque *
                            </label>
                            <input
                                type="text"
                                value={formData.make}
                                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Toyota, Renault..."
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Modèle *
                            </label>
                            <input
                                type="text"
                                value={formData.model}
                                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Corolla, Clio..."
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Année
                            </label>
                            <input
                                type="number"
                                value={formData.year}
                                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                min="2000"
                                max={new Date().getFullYear() + 1}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Catégorie
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Prix/Jour (DZD) *
                            </label>
                            <input
                                type="number"
                                value={formData.daily_rate}
                                onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="5000"
                                min="0"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Ville *
                            </label>
                            <input
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Alger, Oran..."
                                required
                            />
                        </div>
                    </div>

                    {/* Features */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Équipements
                        </label>

                        {/* Common Features */}
                        <div className="flex flex-wrap gap-2 mb-3">
                            {commonFeatures.map(feature => (
                                <button
                                    key={feature}
                                    type="button"
                                    onClick={() => addCommonFeature(feature)}
                                    disabled={formData.features.includes(feature)}
                                    className={`px-3 py-1 rounded-full text-sm transition-all ${formData.features.includes(feature)
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                >
                                    {feature}
                                </button>
                            ))}
                        </div>

                        {/* Custom Feature Input */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newFeature}
                                onChange={(e) => setNewFeature(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                                className="flex-1 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Ajouter un équipement personnalisé..."
                            />
                            <button
                                type="button"
                                onClick={addFeature}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Ajouter
                            </button>
                        </div>

                        {/* Selected Features */}
                        {formData.features.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {formData.features.map(feature => (
                                    <div
                                        key={feature}
                                        className="px-3 py-1 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-300 text-sm flex items-center gap-2"
                                    >
                                        {feature}
                                        <button
                                            type="button"
                                            onClick={() => removeFeature(feature)}
                                            className="hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Création...' : 'Créer le véhicule'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateVehicleModal;
